import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  apiRequest,
  configureAdminApiAuth,
  ApiError,
} from "../services/api";
import { clearErpTabId, getErpTabId } from '../services/erpTabSession';
import { applyAuthorizationSnapshot } from '../utils/authorizationSnapshot';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Refreshes update this ref only, so the dashboard does not re-render.
  const accessTokenRef = useRef(null);
  const refreshPromiseRef = useRef(null);
  const generationRef = useRef(0);

  const clearSession = useCallback(() => {
    generationRef.current++;
    clearErpTabId();
    accessTokenRef.current = null;
    setAccessToken(null);
    setUser(null);
  }, []);

  const loadUser = useCallback(async (token) => {
    const response = await apiRequest("/auth/me", {
      accessToken: token,
    });

    const currentUser = response?.user || response;
    setUser(currentUser);
    return currentUser;
  }, []);

  const refreshAccessToken = useCallback(
    async ({ syncReactState = false } = {}) => {
      if (!getErpTabId()) throw new ApiError('Please sign in.', 401);
      const generation = generationRef.current;
      if (!refreshPromiseRef.current) refreshPromiseRef.current = apiRequest("/auth/refresh", {
        method: "POST",
        skipAuthRefresh: true,
        retryOnUnauthorized: false,
      }).finally(() => { refreshPromiseRef.current = null; });
      const response = await refreshPromiseRef.current;
      if (generation !== generationRef.current) throw new ApiError('Session ended.',401);

      if (!response?.accessToken) {
        throw new Error("Refresh token did not return an access token.");
      }

      accessTokenRef.current = response.accessToken;
      // Keep UI authorization current on the existing refresh response, without
      // another HTTP call or a dashboard re-render when nothing has changed.
      if (response.authorization) setUser(current => applyAuthorizationSnapshot(current, response.authorization));

      // Use this only during initial app restore—not background refreshes.
      if (syncReactState) {
        setAccessToken(response.accessToken);
      }

      return response.accessToken;
    },
    [],
  );

  useEffect(() => {
    return configureAdminApiAuth({
      getAccessToken: () => accessTokenRef.current,
      refreshAccessToken,
      onRefreshFailure: clearSession,
    });
  }, [clearSession, refreshAccessToken]);

  const login = useCallback(
    async ({ identifier, password }) => {
      getErpTabId(true);
      const response = await apiRequest("/auth/login", {
        method: "POST",
        body: { identifier, password },
        skipAuthRefresh: true,
        retryOnUnauthorized: false,
      });

      accessTokenRef.current = response.accessToken;
      setAccessToken(response.accessToken);

      return loadUser(response.accessToken);
    },
    [loadUser],
  );

  const logout = useCallback(async () => {
    try {
      await apiRequest("/auth/logout", {
        method: "POST",
        accessToken: accessTokenRef.current || accessToken,
        skipAuthRefresh: true,
        retryOnUnauthorized: false,
      });
    } finally {
      clearSession();
    }
  }, [accessToken, clearSession]);

  useEffect(() => {
    async function restoreSession() {
      try {
        const token = await refreshAccessToken({
          syncReactState: true,
        });

        await loadUser(token);
      } catch (error) {
        if ([401,403].includes(error.status)) clearSession();
      } finally {
        setIsLoading(false);
      }
    }

    restoreSession();
  }, [clearSession, loadUser, refreshAccessToken]);

  useEffect(() => {
    if (!accessToken) return undefined;

    const refreshSilently = async () => {
      try {
        await refreshAccessToken();
      } catch (error) {
        if ([401,403].includes(error.status)) clearSession();
      }
    };

    // Keep 1 minute while testing. Change to 12 * 60 * 1000 for production.
    const intervalId = window.setInterval(refreshSilently, 60 * 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshSilently();
      }
    };

    document.addEventListener(
      "visibilitychange",
      handleVisibilityChange,
    );
    window.addEventListener('online', refreshSilently);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('online', refreshSilently);
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange,
      );
    };
  }, [accessToken, clearSession, refreshAccessToken]);

  const value = useMemo(
    () => ({
      accessToken,
      user,
      isLoading,
      isAuthenticated: Boolean(accessToken && user),
      login,
      logout,
    }),
    [accessToken, user, isLoading, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }

  return context;
}
